import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import useProject from "../../components/hooks/useProject";

import { useToast } from "../../components/hooks/use-toast";
import { useAiModel } from "../../components/hooks/useAiModel";

import { CommonDialog } from "../../components/CommonDialog";

import ProjectPropertiesBreadcrumbs from "./ProjectPropertiesBreadcrumbs";
import ProjectPropertiesTabs from "./ProjectPropertiesTabs";

import functionsService from "../../services/functionsService";
import firestoreService from "../../services/firestoreService";
import analyticsService from "../../services/analyticsService";

const ProjectProperties = () => {
  const { projectId } = useParams();
  const { project } = useProject(projectId);

  const [isUpdatingProject, setIsUpdatingProject] = useState(false);
  const [showContextGenerationInfo, setShowContextGenerationInfo] =
    useState(false);

  const { toast } = useToast();
  const navigate = useNavigate();
  const [selectedAiModel] = useAiModel();

  const [properties, setProperties] = useState({
    projectTypes: [],
    projectDescription: "",
    projectCost: "",
    projectLifecycles: [],
    locations: [],
    locationTypes: [],
    operatingConditions: [],
    users: [],
    systems: [],
    operatingModes: [],
    standards: [],
    disciplines: [],
    drawingPackages: [],
    mitigationOwners: [],
    meetings: [],
    generatedContext: "",
  });

  useEffect(() => {
    if (project && project.projectMetadata) {
      const metadata = project.projectMetadata;

      setProperties((prev) => ({
        ...prev,
        projectTypes: metadata.projectTypes || [],
        projectDescription: metadata.projectDescription || "",
        projectCost: metadata.projectCost || "",
        projectLifecycles: metadata.projectLifecycles || [],
        locations: metadata.locations || [],
        locationTypes: metadata.locationTypes || [],
        operatingConditions: metadata.operatingConditions || [],
        users: metadata.users || [],
        systems: metadata.systems || [],
        operatingModes: metadata.operatingModes || [],
        standards: metadata.standards || [],
        disciplines: metadata.disciplines || [],
        drawingPackages: metadata.drawingPackages || [],
        mitigationOwners: metadata.mitigationOwners || [],
        meetings: metadata.meetings || [],
      }));
    }
  }, [project]);

  const updateProject = async () => {
    setIsUpdatingProject(true);

    try {
      await firestoreService.update("projects", project.id, {
        projectMetadata: properties,
      });
      
      // Track project metadata update in analytics
      analyticsService.trackProjectUpdate(project.id, 'metadata');

      // Navigate back to previous page
      if (project.context) {
        navigate(`/projects/${project.id}/hazards`);
      }

      // Go to context editor
      else {
        setShowContextGenerationInfo(true);
      }
    } catch (error) {
      console.error("Error updating project:", error);
      
      // Track error in analytics
      analyticsService.trackError('project_metadata_update_error', error.message, 'updateProject');

      toast({
        variant: "destructive",
        title: "Error updating project",
        description: error.message,
      });
    } finally {
      setIsUpdatingProject(false);
    }
  };

  const generateContext = async () => {
    try {
      // Ensure we have required data
      if (
        !project.projectMetadata.projectTypes ||
        project.projectMetadata.projectTypes.length === 0 ||
        !project.projectMetadata.projectDescription
      ) {
        toast({
          variant: "destructive",
          title: "Missing Required Data",
          description:
            "Project types and description are required to generate context.",
        });
        return;
      }

      const { success } = await functionsService.generateProjectContext({
        projectId: project.id,
        model: selectedAiModel,
      });

      if (success) {
        // Track AI context generation initiation in analytics
        analyticsService.trackFeatureUsage('generate_context_initiated', selectedAiModel);
        
        navigate(`/projects/${project.id}/context`);
      }
    } catch (error) {
      // Track error in analytics
      analyticsService.trackError('context_generation_error', error.message, 'generateContext');
      
      toast({
        variant: "destructive",
        title: "Error generating context",
        description: error.message,
      });
    }
  };

  return (
    <>
      <CommonDialog
        open={showContextGenerationInfo}
        setOpen={setShowContextGenerationInfo}
        title={"Generate project context"}
        description={
          "The project context will now be automatically generated based on the project properties above"
        }
        isYesNo={false}
        onConfirm={generateContext}
      />

      {project && !project.projectMetadata && properties && (
        <ProjectPropertiesBreadcrumbs
          properties={properties}
          setProperties={setProperties}
          onUpdate={updateProject}
          isUpdating={isUpdatingProject}
        />
      )}

      {project && project.projectMetadata && properties && (
        <ProjectPropertiesTabs
          properties={properties}
          setProperties={setProperties}
          onUpdate={updateProject}
          isUpdating={isUpdatingProject}
        />
      )}
    </>
  );
};

export default ProjectProperties;
